import { useState } from 'react';
import { useGT } from 'gt-react';
import type { TaskTypeView } from '@erp/shared-types';
import { rpcClient } from 'src/api/rpc/client';
import { toast } from 'src/components/ui/toast';
import { supplierLineRematchPlans } from './supplierDocumentLine';
import {
  useCommitTaskCorrection,
  type CorrectionCommitResult,
} from './useCommitTaskCorrection';
import {
  getValueAtPath,
  readRowArray,
  rowObject,
  setValueAtPath,
  type TaskTypeViewSlot,
} from './taskTypeViewValue';

type Commit = (
  next: Record<string, unknown>,
) => Promise<CorrectionCommitResult>;

type MatchRowInput = Parameters<
  typeof rpcClient.accountsPayable.supplierDocumentLines.rematch
>[0]['lines'][number];

/**
 * The commit a task view uses. It saves the correction, and first matches
 * supplier-line rows again whenever the commit changes the supplier those
 * rows belong to. The rematched rows and the new supplier land in one
 * correction write, so the page never shows rows that were matched for the
 * previous supplier. A rematch failure still saves the supplier change and
 * says the rows were left alone.
 */
export function useTaskViewCommit(
  task: { id: string; updatedAt: Date },
  slot: TaskTypeViewSlot,
  workingCopy: Record<string, unknown>,
): { commit: Commit; rematching: boolean } {
  const gt = useGT();
  const commit = useCommitTaskCorrection(task);
  const view: TaskTypeView | null = slot.kind === 'ready' ? slot.view : null;
  // While rows are being matched again the view is inert: a blur-commit
  // built from the pre-rematch copy would race this save for the task's
  // version and one of them would lose.
  const [rematching, setRematching] = useState(false);

  async function commitWithRematch(next: Record<string, unknown>) {
    const plans = view ? supplierLineRematchPlans(view, workingCopy, next) : [];
    if (plans.length === 0) return commit(next);
    // try/finally without catch is not yet lowered by the React Compiler.
    setRematching(true);
    let result: CorrectionCommitResult;
    try {
      result = await commit(await rematched(next, plans));
    } catch (error) {
      setRematching(false);
      throw error;
    }
    setRematching(false);
    return result;
  }

  async function rematched(
    next: Record<string, unknown>,
    plans: ReturnType<typeof supplierLineRematchPlans>,
  ): Promise<Record<string, unknown>> {
    let staged = next;
    for (const plan of plans) {
      const rows = readRowArray(getValueAtPath(staged, plan.path));
      try {
        const result =
          await rpcClient.accountsPayable.supplierDocumentLines.rematch({
            supplierId: plan.supplierId,
            lines: rows.map(toMatchRowInput),
          });
        staged = setValueAtPath(staged, plan.path, result.lines);
      } catch {
        toast.error(
          gt(
            'The supplier was changed but the lines could not be matched again.',
            {
              context:
                'toast when rematching supplier document lines after a supplier change fails',
            },
          ),
        );
      }
    }
    return staged;
  }

  return { commit: commitWithRematch, rematching };
}

function toMatchRowInput(row: unknown, position: number): MatchRowInput {
  const record = rowObject(row);
  const index =
    typeof record.index === 'number' && Number.isInteger(record.index)
      ? record.index
      : position;
  const extracted = rowObject(record.extracted);
  return { ...record, index, extracted };
}
